import React, { useMemo } from "react";
import { Box, Text } from "@chakra-ui/layout";
import { CherryPayApi } from "../../api/models";
import { IconButton, Table, Tbody, Td, Th, Thead, Tr } from "@chakra-ui/react";
import { Menu, MenuButton, MenuItem, MenuList } from "@chakra-ui/menu";

import { CircleIcon, MoreHorizontal } from "../../styles/icons";

interface RolesRbacTableProps {
  roles: CherryPayApi.CherryHubRoleSummaryItem[];
  availablePermissions: CherryPayApi.PermissionSummary[];
  onClickView?: (role: CherryPayApi.CherryHubRoleSummaryItem) => void;
  onClickEdit?: (role: CherryPayApi.CherryHubRoleSummaryItem) => void;
  onClickDelete?: (role: CherryPayApi.CherryHubRoleSummaryItem) => void;
}

export const RolesRbacTable = ({
  roles,
  availablePermissions,
  onClickView,
  onClickEdit,
  onClickDelete,
}: RolesRbacTableProps) => {
  // Group permissions by GroupName
  const groupedPermissions = useMemo(() => {
    const groups = {
      administration: {
        label: "Administration",
        permissions: [] as CherryPayApi.PermissionSummary[],
      },
      members: {
        label: "Members",
        permissions: [] as CherryPayApi.PermissionSummary[],
      },
      memberInvitations: {
        label: "Member Invitations",
        permissions: [] as CherryPayApi.PermissionSummary[],
      },
      reports: {
        label: "Reports",
        permissions: [] as CherryPayApi.PermissionSummary[],
      },
      other: {
        label: "Other",
        permissions: [] as CherryPayApi.PermissionSummary[],
      },
    };

    availablePermissions.forEach((permission) => {
      const name = permission?.Id?.toLowerCase() ?? "";
      if (name.startsWith("memberinvite")) {
        groups.memberInvitations.permissions.push(permission);
      } else if (name.startsWith("member")) {
        groups.members.permissions.push(permission);
      } else if (name.startsWith("reporting")) {
        groups.reports.permissions.push(permission);
      } else if (name.endsWith("management")) {
        groups.administration.permissions.push(permission);
      } else {
        groups.other.permissions.push(permission);
      }
    });

    return [
      groups.members,
      groups.memberInvitations,
      groups.administration,
      groups.reports,
      groups.other,
    ].filter((group) => group.permissions.length > 0);
  }, [availablePermissions]);

  // Create a set of permissions for each role for quick lookup
  const rolePermissionSets = useMemo(() => {
    return roles.map((role) => ({
      role,
      permissionSet: new Set(role.Permissions),
    }));
  }, [roles]);

  return (
    <Box w="100%" overflowX="auto">
      <Table size="sm" textAlign="center">
        <Thead>
          <Tr>
            <Th></Th>
            {roles.map((role) => (
              <Th
                textAlign="center"
                key={role.RoleId}
                wordBreak="keep-all"
                whiteSpace="nowrap"
                minW="120px"
              >
                {role.DisplayName}
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {groupedPermissions.map(({ label, permissions }, i) => (
            <React.Fragment key={`group-${label}`}>
              <Tr>
                <Th
                  backgroundColor="white"
                  borderBottom="none"
                  textTransform="none"
                  fontSize="xs"
                  lineHeight="2"
                >
                  <Text paddingTop={i === 0 ? undefined : "4"}>{label}</Text>
                </Th>
                {rolePermissionSets.map(({ role }) => (
                  <Td
                    key={role.RoleId}
                    borderBottom="none"
                    borderLeft="1px solid #efefef"
                  />
                ))}
              </Tr>
              {permissions.map((permission) => (
                <Tr key={permission.Id}>
                  <Th
                    paddingLeft="8"
                    width="0.1%"
                    whiteSpace="nowrap"
                    backgroundColor="white"
                    borderBottom="none"
                    textTransform="none"
                    fontWeight="400"
                    fontSize="xs"
                    lineHeight="2"
                  >
                    {permission.DisplayName}
                  </Th>
                  {rolePermissionSets.map(({ role, permissionSet }) => (
                    <Td
                      textAlign="center"
                      key={`${permission.Id}-${role.RoleId}`}
                      borderBottom="none"
                      borderLeft="1px solid #efefef"
                    >
                      {permission.Id && permissionSet.has(permission.Id) ? (
                        <CircleIcon color="cherryUi.600" boxSize="10px" />
                      ) : (
                        ""
                      )}
                    </Td>
                  ))}
                </Tr>
              ))}
            </React.Fragment>
          ))}

          <Tr>
            <Th backgroundColor="white" borderBottom="none"></Th>
            {rolePermissionSets.map(({ role }) => (
              <Td
                key={role.RoleId}
                textAlign="center"
                borderBottom="none"
                borderLeft="1px solid #efefef"
              >
                <Menu>
                  <MenuButton
                    icon={<MoreHorizontal />}
                    variant="ghost"
                    as={IconButton}
                    fontSize="sm"
                  ></MenuButton>
                  <MenuList>
                    {role.IsSystemRole ? (
                      <MenuItem onClick={() => onClickView?.(role)}>
                        View role
                      </MenuItem>
                    ) : (
                      <>
                        <MenuItem onClick={() => onClickEdit?.(role)}>
                          Edit role
                        </MenuItem>
                        <MenuItem onClick={() => onClickDelete?.(role)}>
                          Delete role
                        </MenuItem>
                      </>
                    )}
                  </MenuList>
                </Menu>
              </Td>
            ))}
          </Tr>
        </Tbody>
      </Table>
    </Box>
  );
};
